/**
 * Film Festival hub listing buckets — mutually exclusive membership.
 *
 * - now_showing: now >= start AND now <= end (published list items only)
 * - previous: now > end
 * - upcoming: now < start
 *
 * Missing start → already started; missing end → not yet ended.
 * Both missing → is_active ? now_showing : previous.
 *
 * Prefer server listing_bucket when present; otherwise classify from dates.
 */

export const LISTING_NOW_SHOWING = "now_showing";
export const LISTING_PREVIOUS = "previous";
export const LISTING_UPCOMING = "upcoming";

function parseInstant(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d;
  } catch (e) {
    return null;
  }
}

function isTruthyActive(value) {
  return value === true || value === 1 || value === "1";
}

/**
 * @param {object} edition
 * @param {Date} [now]
 * @returns {string}
 */
export function classifyEditionListing(edition, now) {
  if (!edition) return LISTING_PREVIOUS;
  const server = edition.listing_bucket;
  if (
    server === LISTING_NOW_SHOWING ||
    server === LISTING_PREVIOUS ||
    server === LISTING_UPCOMING
  ) {
    return server;
  }

  // Avoid instanceof Date — fails across VM realms in contract tests.
  const at =
    now && typeof now.getTime === "function" && !Number.isNaN(now.getTime())
      ? now
      : new Date();
  const start = parseInstant(edition.starts_at);
  const end = parseInstant(edition.ends_at);

  if (start && at.getTime() < start.getTime()) {
    return LISTING_UPCOMING;
  }
  if (end && at.getTime() > end.getTime()) {
    return LISTING_PREVIOUS;
  }
  if (!start && !end) {
    return isTruthyActive(edition.is_active)
      ? LISTING_NOW_SHOWING
      : LISTING_PREVIOUS;
  }
  return LISTING_NOW_SHOWING;
}

/**
 * Partition into exclusive collections — never "all except current".
 *
 * @param {object[]} list
 * @param {Date} [now]
 * @returns {{ nowShowing: object[], upcoming: object[], previous: object[] }}
 */
export function partitionFestivalEditions(list, now) {
  const nowShowing = [];
  const upcoming = [];
  const previous = [];
  const seen = new Set();
  const rows = Array.isArray(list) ? list : [];

  rows.forEach((edition) => {
    const key =
      edition && (edition.id != null || edition.slug)
        ? String(edition.id != null ? edition.id : edition.slug)
        : null;
    if (key && seen.has(key)) {
      return;
    }
    if (key) seen.add(key);

    const bucket = classifyEditionListing(edition, now);
    if (bucket === LISTING_NOW_SHOWING) {
      nowShowing.push(edition);
    } else if (bucket === LISTING_UPCOMING) {
      upcoming.push(edition);
    } else {
      previous.push(edition);
    }
  });

  return { nowShowing, upcoming, previous };
}

/**
 * Assert no edition id/slug appears in more than one bucket (test helper).
 * @param {{ nowShowing: object[], upcoming: object[], previous: object[] }} parts
 * @returns {boolean}
 */
export function listingBucketsAreExclusive(parts) {
  const keys = [];
  ["nowShowing", "upcoming", "previous"].forEach((name) => {
    (parts[name] || []).forEach((ed) => {
      keys.push(String(ed.id != null ? ed.id : ed.slug));
    });
  });
  return keys.length === new Set(keys).size;
}
